import { useEffect, useRef, useState } from 'react';
import { UNIT_PATH_POSITION_ANCHOR_ID } from '../hooks/useDashboardUnitPath';

// Height of the app's fixed header; the visible viewport starts below it, so a target whose bottom is
// above this line counts as scrolled off the top.
const HEADER_OFFSET = 18;
// Gap left below the fixed header when the click scrolls the target connector to the top of the view.
const SCROLL_TOP_MARGIN = 170;

// Where the student's current position sits relative to the viewport: scrolled off the top ('above'),
// below the fold ('below'), on screen ('in-view'), or not rendered at all ('missing').
export type UnitPathPositionState = 'above' | 'below' | 'in-view' | 'missing';

export interface UseUnitPathPositionAnchorResult {
  state: UnitPathPositionState;
  // Whether the position is off screen ('above' | 'below'), i.e. whether there is anywhere to scroll
  // back to. Every "Back to position" control keys its visibility off this so they stay in lockstep.
  isOutOfView: boolean;
}

// Scrolls the viewport to the student's current position on the unit path — the connector tagged
// UNIT_PATH_POSITION_ANCHOR_ID, landed near the top just below the fixed header — or to the very top
// of the page when nothing has been passed yet (there is no connector to land on). Shared by the
// rail's "Back to position" button, the locked-skill modal and the Dashboard's auto-scroll-on-entry.
export const scrollToUnitPathPosition = (hasPassedConnector: boolean): void => {
  if (!hasPassedConnector) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  const target = document.getElementById(UNIT_PATH_POSITION_ANCHOR_ID);
  if (!target) {
    return;
  }

  const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET - SCROLL_TOP_MARGIN;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
};

// How long a caller that fires the scroll from outside the dashboard keeps waiting for the unit path
// to render its anchor, and how often it re-checks. Bounded so a click made on another route never
// leaves a scroll pending indefinitely.
const ANCHOR_WAIT_TIMEOUT = 2000;
const ANCHOR_POLL_INTERVAL = 100;

// Scrolls to the student's current position as soon as the unit path has rendered its anchor. For
// callers firing from outside the unit path — e.g. the weekly-goal panel's "Give me a skill" link,
// which navigates to the dashboard first — where the anchor does not exist yet at click time. When the
// student is already on the dashboard the anchor is there and this scrolls straight away.
export const scrollToUnitPathPositionWhenReady = (): void => {
  const startedAt = Date.now();

  const attempt = (): void => {
    // The anchor is only rendered by the unit path itself, and it always lands on something to scroll
    // to (the position connector, or the first card when nothing has been passed yet), so its presence
    // is enough — there is never a need for the scroll-to-top fallback.
    if (document.getElementById(UNIT_PATH_POSITION_ANCHOR_ID)) {
      scrollToUnitPathPosition(true);
      return;
    }
    if (Date.now() - startedAt < ANCHOR_WAIT_TIMEOUT) {
      window.setTimeout(attempt, ANCHOR_POLL_INTERVAL);
    }
  };

  attempt();
};

// Reads where the "Back to position" target currently sits. The single source of truth for the
// reactive hook below (which control to show, and pointing which way) and for one-off callers such as
// the locked-skill modal's "Go to recommended" action.
export const getUnitPathPositionState = (): UnitPathPositionState => {
  const rect = document.getElementById(UNIT_PATH_POSITION_ANCHOR_ID)?.getBoundingClientRect();
  if (!rect) {
    return 'missing';
  }
  if (rect.bottom < HEADER_OFFSET) {
    return 'above';
  }
  return rect.top > window.innerHeight ? 'below' : 'in-view';
};

// 'above' and 'below' both mean the target is off screen. The one rule every "Back to position"
// control follows, so none of them can contradict another about whether there is anywhere to scroll.
const isStateOutOfView = (state: UnitPathPositionState): boolean => state === 'above' || state === 'below';

// True when the "Back to position" target is scrolled out of the viewport. Lets callers (e.g. the
// locked-skill modal's "Go to recommended" action) skip the scroll when the target is already visible.
export const isUnitPathPositionOutOfView = (): boolean => isStateOutOfView(getUnitPathPositionState());

/**
 * Tracks the student's current position on the unit path: whether the position anchor is above, below
 * or on screen, and — derived from that — whether it is off screen at all. The single reactive source
 * for every control that
 * follows the position: the rail's "Back to position" button and the unit header's "Back to my
 * position" one, which therefore appear and disappear together.
 *
 * Native scroll / resize plus a rAF-throttled MutationObserver, matching the dashboard house style
 * (useActiveUnitPathSegment): consumers mount before the unit path renders its anchor, and the anchor
 * moves as skills are passed, so without the observer a reading could stay stale until the student
 * happened to scroll.
 */
export const useUnitPathPositionAnchor = (): UseUnitPathPositionAnchorResult => {
  const [result, setResult] = useState<UseUnitPathPositionAnchorResult>({
    state: 'missing',
    isOutOfView: false,
  });
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const update = (): void => {
      const state = getUnitPathPositionState();

      setResult(current => (current.state === state ? current : { state, isOutOfView: isStateOutOfView(state) }));
    };

    const scheduleUpdate = (): void => {
      if (rafIdRef.current !== null) {
        return;
      }
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        update();
      });
    };

    // Correct on first paint, then follow scroll / resize and any re-render of the path.
    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      observer.disconnect();
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  return result;
};
